import { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';
import { useReqireLabel, useTracking, getLabel } from '../utils';
import { ScreenReaderOnly } from '..';
import type { TrackingProp } from '../types';
import styles from '../input/input.module.css';
import clsx from 'clsx';

const WARNING_TEXTS = {
  no: 'bokstav for mye',
  da: 'bogstav for meget',
  sv: 'bokstav för mycket',
  en: 'character too many'
};

export type TextareaProps = React.ComponentPropsWithoutRef<'textarea'> & {
  classNameWrapper?: string;
  invalid?: boolean;
  internal?: 'small' | false;
  maxWarning?: number;
  tracking?: TrackingProp;
  wrapperProps?: React.ComponentPropsWithoutRef<'span'>;
};

let valueOnFocus: string; // Used to check if there is actually a change between focus and blur
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    {
      classNameWrapper: deprecated,
      hidden,
      invalid,
      maxWarning = 0,
      onBlur,
      onFocus,
      onInput,
      internal = false,
      tracking = true,
      wrapperProps,
      ...rest
    },
    ref
  ) {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    const track = useTracking(tracking);
    const classes = clsx(
      wrapperProps?.className,
      styles.wrapper,
      deprecated,
      !!rest.readOnly && styles.isReadOnly,
      !!rest.disabled && styles.isDisabled,
      !!invalid && styles.isInvalid,
      !!internal && styles[`${internal}`]
    );

    const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      onFocus?.(event);
      valueOnFocus = event.target.value;
    };
    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
      if (valueOnFocus !== event.target.value)
        track({
          eventCategory: 'textarea',
          eventAction: 'change',
          eventLabel: getLabel(event.target)
        });
      onBlur?.(event);
    };

    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement); // Forward innerRef
    useReqireLabel<HTMLTextAreaElement>(innerRef);
    useEffect(() => {
      if (innerRef.current) updateCount(innerRef.current, maxWarning); // Update count on re-render
    });

    return (
      <span
        {...wrapperProps} // Must be before className
        className={classes}
        hidden={hidden}
      >
        <textarea
          data-internal-small={internal === 'small' ? true : undefined}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onInput={(event) => {
            updateCount(event.currentTarget, maxWarning); // Update count on typing
            onInput?.(event);
          }}
          ref={innerRef}
          {...rest}
        />
        <ScreenReaderOnly aria-live="polite"></ScreenReaderOnly>
      </span>
    );
  }
);

const updateCount = (self: HTMLTextAreaElement, max: number) => {
  const count = self.value.length || 0;
  const rows = self.hasAttribute('rows');
  const wrap = self.parentElement as HTMLElement;
  const live = self.nextElementSibling as HTMLElement;

  self.style.height = ''; // Reset to natural height
  const height = self.scrollHeight; // Get the scroll height
  if (!rows && height) self.style.height = `${height}px`; // Only set height if not hidden (causing height = 0)
  if (max) wrap.setAttribute('data-count', `${count}/${max}`);
  if (max) wrap.toggleAttribute('data-count-warning', count > max);
  if (count > max) {
    const lang = wrap.closest('[lang]')?.getAttribute('lang') || 'no';
    const text = `${count - max} ${WARNING_TEXTS[lang as keyof typeof WARNING_TEXTS]}`;
    if (live.textContent !== text) live.textContent = text; // Update screen reader text after announcing typed character
  }
};
